/**
 * One refine turn's rubric diff, as a ticklist rather than a prose sentence —
 * PIVOT-109. Renders exactly the fields the server named as `changed` (its
 * list is authoritative for *which* fields moved — computed server-side
 * against the stored round-trip, `rubric_refiner.changed_fields`). This
 * component only decides *how* to show each one: added/dropped lines for the
 * independent-rule fields, before → after for everything else.
 *
 * **Undo is a real save, not client state.** Tapping it calls `onUndo`, which
 * the hosting page (`Profile.tsx`) wires to a genuine `PUT /api/profile`
 * restoring this one field to its pre-turn value. A rejected `onUndo` must
 * leave the row visibly un-undone — flipping to "undone" on a request that
 * failed would be exactly the silent-success bug the constitution rules out.
 * The page owns the failure's error copy (one error style for every save on
 * this screen), so this component's `catch` does nothing but stop blocking
 * the button.
 */

import { useState } from 'react'
import { useI18n } from '../i18n'
import type { TranslationKey } from '../i18n/types'
import type { CommuteRule, Rubric, RubricField, TechnicalDepth } from '../types/domain'
import './RubricDiff.css'

/**
 * What `changed` names, in the candidate's words — the same labels
 * `RubricView` puts on those fields. Used to live on `RefineChat.tsx`
 * (`ChangeNote`'s field list); moved here because this is now the only place
 * that reports a field by name — the chat log just threads the diff through.
 */
// eslint-disable-next-line react-refresh/only-export-components -- a lookup table, not a component
export const FIELD_LABELS: Record<RubricField, TranslationKey> = {
  summary: 'profile.rubricSummary',
  must_haves: 'profile.rubricMustHaves',
  deal_breakers: 'profile.rubricDealBreakers',
  strengths: 'profile.rubricStrengths',
  proven_but_unwanted: 'profile.rubricProvenUnwanted',
  commute: 'profile.rubricCommute',
  experience: 'profile.rubricAxisExperience',
  values: 'profile.rubricAxisValues',
  salary: 'profile.rubricAxisSalary',
  lateral_directions: 'profile.rubricLateral',
  technical_depth: 'profile.rubricTechnicalDepth',
}

/**
 * The four fields that are sets of independent rules — diffed by what got
 * added or dropped. Everything else is a single value (a sentence, a
 * weighted rule, a structured commute) that only makes sense as one
 * before/after pair, never a set difference.
 */
type ListRubricField =
  | 'must_haves'
  | 'deal_breakers'
  | 'proven_but_unwanted'
  | 'lateral_directions'

const LIST_FIELDS = new Set<ListRubricField>([
  'must_haves',
  'deal_breakers',
  'proven_but_unwanted',
  'lateral_directions',
])

function isListField(field: RubricField): field is ListRubricField {
  return LIST_FIELDS.has(field as ListRubricField)
}

interface RubricDiffProps {
  /** The server's list of what this turn changed — authoritative for which
      rows render at all. */
  changed: RubricField[]
  /** The rubric as it stood right before this turn was sent (captured
      client-side; see `RefineChat`'s `rubric` prop). */
  before: Rubric
  /** The rubric the server returned after this turn. */
  after: Rubric
  /** Fields already undone in this turn. Lives in the parent (`RefineChat`)
      because a turn — and therefore this state — disappears on reload with
      the rest of the ephemeral chat log; this component owns none of it. */
  undoneFields: RubricField[]
  /** Restore one field to its `before` value via a real save. Rejects on
      failure. */
  onUndo: (field: RubricField) => Promise<void>
}

export function RubricDiff({ changed, before, after, undoneFields, onUndo }: RubricDiffProps) {
  const { t } = useI18n()
  // Blocks a second tap on the same field's button while its save is in
  // flight; a rejection just clears it below, since a failed undo leaves the
  // row exactly where it was.
  const [pending, setPending] = useState<RubricField | null>(null)

  const handleUndo = async (field: RubricField) => {
    setPending(field)
    try {
      await onUndo(field)
    } catch {
      // The page already surfaced its own error copy for this; nothing else
      // to do here but stop blocking the button.
    } finally {
      setPending((current) => (current === field ? null : current))
    }
  }

  return (
    <div className="rubric-diff">
      {changed.map((field) => (
        <div className="rubric-diff-field" key={field}>
          <h3 className="rubric-diff-field-label">{t(FIELD_LABELS[field])}</h3>
          {isListField(field) ? (
            <ListFieldDiff
              before={formatListField(field, before)}
              after={formatListField(field, after)}
            />
          ) : (
            <ScalarLine before={formatField(field, before)} after={formatField(field, after)} />
          )}
          {undoneFields.includes(field) ? (
            <p className="rubric-diff-undone">{t('profile.refineUndone')}</p>
          ) : (
            <button
              type="button"
              className="btn btn-ghost rubric-diff-undo"
              disabled={pending === field}
              onClick={() => void handleUndo(field)}
            >
              {t('profile.refineUndo')}
            </button>
          )}
        </div>
      ))}
      <p className="rubric-diff-applied">{t('profile.refineApplied')}</p>
    </div>
  )
}

function formatListField(field: ListRubricField, rubric: Rubric): string[] {
  if (field === 'must_haves' || field === 'deal_breakers') {
    return rubric[field].map((line) => line.text)
  }
  return rubric[field]
}

/**
 * Added (`+`) and dropped (`−`) lines by set difference. Field content is
 * candidate/model-authored free text, not app UI copy, so — like every rule
 * `RubricView` prints — it renders as-is rather than going through `t()`.
 *
 * The server names a field "changed" whenever the two round-tripped lists
 * differ at all, which includes a same-set reorder — a case a set diff
 * renders as nothing added and nothing dropped. Falling back to the plain
 * before/after line keeps that turn from looking like a blank, broken row.
 */
function ListFieldDiff({ before, after }: { before: string[]; after: string[] }) {
  const added = after.filter((item) => !before.includes(item))
  const dropped = before.filter((item) => !after.includes(item))

  if (added.length === 0 && dropped.length === 0) {
    return <ScalarLine before={before.join(', ')} after={after.join(', ')} />
  }

  return (
    <ul className="rubric-diff-lines">
      {dropped.map((item) => (
        <li key={`-${item}`} className="rubric-diff-line rubric-diff-line--dropped">
          {item}
        </li>
      ))}
      {added.map((item) => (
        <li key={`+${item}`} className="rubric-diff-line rubric-diff-line--added">
          {item}
        </li>
      ))}
    </ul>
  )
}

function ScalarLine({ before, after }: { before: string; after: string }) {
  return (
    <p className="rubric-diff-scalar">
      <span className="rubric-diff-before">{before}</span>
      <span className="rubric-diff-after">{after}</span>
    </p>
  )
}

/**
 * One scalar-ish field as a single readable line — the same content
 * `RubricView` shows for it, just condensed to one line for a before/after
 * pair. `strengths` prints its claims only (evidence is what changed least
 * often and doubles the line's length for little the diff needs).
 *
 * The `must_haves`-shaped cases return early for `switch` exhaustiveness: a
 * `RubricField` added later without updating `LIST_FIELDS` above fails to
 * compile here instead of silently rendering a blank row.
 */
function formatField(field: RubricField, rubric: Rubric): string {
  switch (field) {
    case 'summary':
      return rubric.summary
    case 'strengths':
      return rubric.strengths.map((s) => s.claim).join(', ')
    case 'commute':
      return formatCommute(rubric.commute)
    case 'experience':
      return rubric.experience.description
    case 'values':
      return rubric.values.description
    case 'salary':
      return rubric.salary.description
    case 'technical_depth':
      return formatTechnicalDepth(rubric.technical_depth)
    case 'must_haves':
    case 'deal_breakers':
      return rubric[field].map((line) => line.text).join(', ')
    case 'proven_but_unwanted':
    case 'lateral_directions':
      return rubric[field].join(', ')
  }
}

/** Same parts `RubricView`'s `CommuteRow` shows, joined to one line instead
    of separate pills — there is no reusable formatter to import (those parts
    are built inline in a component this ticket does not touch), so this
    mirrors the same shape rather than inventing a different one. */
function formatCommute(commute: CommuteRule): string {
  const parts: string[] = []
  if (commute.anchor) parts.push(commute.anchor)
  if (commute.max_minutes !== null) parts.push(`${commute.max_minutes}min`)
  if (commute.modes.length > 0) parts.push(commute.modes.join('/'))
  if (commute.regions.length > 0) parts.push(commute.regions.join('/'))
  return parts.join(' · ')
}

/** No screen in the app names `TechnicalDepthLevel`'s values yet (`RubricView`
    does not render `technical_depth` at all — PIVOT-104 decides what the
    judge does with it first), so there is no existing label to reuse. The raw
    level reads acceptably next to its evidence; revisit once one exists. */
function formatTechnicalDepth(depth: TechnicalDepth): string {
  return depth.evidence ? `${depth.level} — ${depth.evidence}` : depth.level
}
